/**
 * The SERP vocabulary contract, split into the three claims it actually makes.
 *
 * FW-URL-14 defines three tones — in the passage that matches the winning
 * answer shape, elsewhere on the page, nowhere — and the middle one is the
 * whole point. A word you use somewhere is a PLACEMENT decision; a word you use
 * nowhere is a content gap. The review card used to show the third group alone,
 * under "Never used on your page", which collapsed two different findings into
 * one instruction and stated it about a page-wide absence it had not measured.
 *
 * The green tone is deliberately NOT one of the groups here. A term already in
 * the passage that matches the winning shape asks nothing of the operator, and
 * the review queue is where work is decided and handed off — a line that says
 * "nothing to do" costs a scan and returns no decision. Worse, on a card whose
 * badge reads STRUCTURE GAP it sits above the real finding looking like a
 * second one. The full three-tone contract stays on url-detail, which is where
 * you go to see what the analysis actually found.
 *
 * Grouping only. Which tone a term carries is decided server-side across every
 * crawled section (`serpTermPlacement`), so the surfaces cannot disagree about
 * whether a word is on the page.
 */

export type ReviewVocabTone = 'in_passage' | 'on_page' | 'absent';

export interface ReviewVocabChip {
  term: string;
  tone: ReviewVocabTone;
}

export interface ReviewAnswerVocab {
  /** Written somewhere else on the page — a placement call. */
  onPage: string[];
  /** Not written anywhere. The only group that is a gap. */
  missing: string[];
}

/** How many terms one group may show before the line stops being scannable. */
export const REVIEW_VOCAB_PER_GROUP = 6;

/**
 * Null when there is nothing to say, so the caller renders no line at all
 * rather than an empty one. Order within a group is the contract's own — the
 * order terms were mined in, strongest first.
 */
export function reviewAnswerVocabGroups(
  chips: readonly ReviewVocabChip[] | null | undefined
): ReviewAnswerVocab | null {
  const pick = (tone: ReviewVocabTone) =>
    (chips || [])
      .filter((chip) => chip?.tone === tone && (chip.term || '').trim())
      .map((chip) => chip.term)
      .slice(0, REVIEW_VOCAB_PER_GROUP);

  const groups: ReviewAnswerVocab = {
    onPage: pick('on_page'),
    missing: pick('absent')
  };

  // Null when both are empty — which now includes the common case of a page
  // that already uses every term in the right place. That card shows no
  // vocabulary line at all, which is the correct amount to say about it.
  return groups.onPage.length || groups.missing.length ? groups : null;
}
